import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree, Route, UrlSegment, Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { Observable } from 'rxjs';
import { Feature } from '../constants/feature-constants';
import { DeviceService } from '../services/device.service';

/**
 * Checks whether the activeDevice supports the features that are given in the route data (in the array `features`).
 * In the feature is not supported by the device, the navigation fails.
 * 
 * The required features can also be set in an ancestor route. But descendants overwrite the required features.
 * 
 * ```typescript
 * data: {
 *   // Features that a device must support to enter the route
 *   features: [Feature.GPS, Feature.XYZ]
 * },
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class FeatureSupportedGuard  {
  constructor(private router: Router, private deviceService: DeviceService, private log: NGXLogger) {}

  canActivate(
    next: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
      // Check if any features are defined in the requested route itself or any parent route
      let features = next.data.features || [];
      let parent: ActivatedRouteSnapshot = next.parent;

      while (features.length === 0 && parent) {
        features = parent.data.features || [];
        parent = parent.parent;
      }

      if (features.length > 0 && this.deviceService.activeDevice?.device) {
        const result = features.some((feature: Feature) => this.deviceService.isFeatureSupported(feature));

        if (!result) {
          this.log.warn(`The device does not support the features at "${state.url}". Access forbidden.`);
          return this.router.parseUrl('');
        } else {
          return true;
        }
      }

      // Allow the route if no feature was defined on any parent route or the route itself (or if there's no active device)
      return true;
  }
  canActivateChild(
    next: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.canActivate(next, state);
  }

  canLoad(route: Route, segments: UrlSegment[]): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    // This function can only check the route itself, no parent routes
    if (Array.isArray(route.data.features) && route.data.features.length > 0 && this.deviceService.activeDevice?.device) {
      const result = route.data.features.some((feature: Feature) => this.deviceService.isFeatureSupported(feature));

      if (!result) {
        // this.router.navigate(['']);
        this.log.warn(`The device does not support the features at "${segments}". Access forbidden.`);
        return this.router.parseUrl('');
      } else {
        return true;
      }
    }

    // Allow loading if no feature was defined on the route (or if there's no active device)
    return true;
  }
}
